import type { SdkConfig } from '../core/types';
import type { Api } from '../core/api';

/**
 * Form tracking — spec §3.3.
 *
 * - `form_start`: first focus on any input/textarea/select inside a <form>.
 * - `form_submit`: native submit event.
 * - `form_abandon`: pagehide fires while a form had `form_start` but no submit.
 *
 * Field VALUES are never captured — only counts and form metadata.
 */
function fieldStats(form: HTMLFormElement): { fields_total: number; fields_filled: number } {
	const fields = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input, textarea, select');
	let filled = 0;
	let total = 0;
	fields.forEach((f) => {
		const type = (f as HTMLInputElement).type;
		if (type === 'submit' || type === 'button' || type === 'hidden') return;
		total++;
		if ((f as HTMLInputElement).value) filled++;
	});
	return { fields_total: total, fields_filled: filled };
}

function describeForm(form: HTMLFormElement): Record<string, unknown> {
	return {
		form_id: form.id || undefined,
		form_name: form.getAttribute('name') || undefined,
		form_action: form.getAttribute('action') || undefined,
		...fieldStats(form),
	};
}

export function installForms(config: SdkConfig, emblue: Api): void {
	if (!config.autoTrack.forms) return;
	if (typeof document === 'undefined') return;

	const started = new WeakSet<HTMLFormElement>();
	const submitted = new WeakSet<HTMLFormElement>();
	const trackedForms: HTMLFormElement[] = [];

	document.addEventListener(
		'focusin',
		(e: FocusEvent) => {
			const target = e.target as Element | null;
			if (!target || target.nodeType !== 1) return;
			if (!(target as HTMLElement).matches?.('input, textarea, select')) return;
			const form = (target as HTMLElement).closest('form') as HTMLFormElement | null;
			if (!form || started.has(form)) return;
			started.add(form);
			trackedForms.push(form);
			emblue.track('form_start', describeForm(form));
		},
		true,
	);

	document.addEventListener(
		'submit',
		(e: SubmitEvent) => {
			const form = e.target as HTMLFormElement | null;
			if (!form || form.tagName !== 'FORM') return;
			submitted.add(form);
			emblue.track('form_submit', describeForm(form));
		},
		true,
	);

	window.addEventListener('pagehide', () => {
		for (const form of trackedForms) {
			if (!submitted.has(form)) {
				emblue.track('form_abandon', describeForm(form));
			}
		}
	});
}
